import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import { API } from '../../../api';
import ConfirmDialog from '../../DialogUi/ConfirmDialog';
import ImproveDialog from '../ImproveDialog';
import RuntimeMeter from '../RuntimeMeter';
import ScenePreview, { ClosingPreview, OpeningPreview } from '../ScenePreview';
import SceneFields from './SceneFields';
import { EXPLAINER_LOCKED_SECONDS, budgetFor, formatApprox, isEditAllowed, itemSeconds } from '../duration';
import { SCENE_TEMPLATES, SCENE_TYPES, sceneProblems, sceneSummaryLine } from '../sceneTemplates';
import { fieldSx, labelSx, pillButtonSx, vb } from '../theme';
import { BrandKit, DEFAULT_BRAND_KIT, SceneType, VideoProject } from '../types';
import { apiCode, apiMessage, useAutosave } from '../useAutosave';

interface LocalScene {
  id: string;
  type: SceneType;
  content: Record<string, any>;
  narration: string;
  reasoning: string | null;
  actualSeconds: number | null;
}

const fromProject = (p: VideoProject): LocalScene[] =>
  p.scenes.map((s) => ({
    id: s.id,
    type: s.type,
    content: s.content,
    narration: s.narration,
    reasoning: s.reasoning,
    actualSeconds: s.audio?.durationSeconds ?? null
  }));

const lockedCardSx = {
  display: 'flex',
  gap: 1.5,
  alignItems: 'center',
  p: 1.5,
  borderRadius: '12px',
  border: `1px dashed ${vb.borderStrong}`,
  background: 'rgba(255,255,255,0.02)'
};

// Explainer storyboard (§33–§36): locked opening, editable scenes (reorder,
// template switch, fields, narration, AI improve, delete, add), locked
// closing, global budget, autosave, view/edit toggle (prototype "Done editing").
const StoryboardEditor: React.FC<{
  project: VideoProject;
  lessonName: string;
  moduleName: string;
  onProject: (p: VideoProject) => void;
  onPreview: () => void;
  onBackToGuidance: () => void;
}> = ({ project, lessonName, moduleName, onProject, onPreview, onBackToGuidance }) => {
  const kit: BrandKit = project.brandKitSnapshot ?? DEFAULT_BRAND_KIT;
  const [scenes, setScenes] = useState<LocalScene[]>(() => fromProject(project));
  const [editing, setEditing] = useState(true);
  const [limitHit, setLimitHit] = useState<string | null>(null);
  const [improveId, setImproveId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const serverRef = useRef(project);
  useEffect(() => {
    if (project.contentVersion !== serverRef.current.contentVersion || project.id !== serverRef.current.id) {
      serverRef.current = project;
      setScenes(fromProject(project));
    }
  }, [project]);

  const budget = budgetFor('explainer', project.targetDurationSeconds);
  const total = useMemo(
    () => Math.round(scenes.reduce((s, x) => s + itemSeconds(x.narration, x.actualSeconds), 0) * 10) / 10,
    [scenes]
  );
  const problems = useMemo(
    () =>
      scenes.map((s) => [...sceneProblems(s.type, s.content), ...(s.narration.trim() ? [] : ['Narration is empty'])]),
    [scenes]
  );
  const incomplete = problems.some((p) => p.length > 0);

  const save = useCallback(
    async (value: LocalScene[]) => {
      const res: any = await API.putVideoProjectScenes({
        projectId: project.id,
        scenes: value.map((s) => ({
          id: s.id,
          type: s.type,
          content: s.content,
          narration: s.narration,
          reasoning: s.reasoning
        }))
      });
      serverRef.current = res.data;
      onProject(res.data as VideoProject);
    },
    [project.id, onProject]
  );
  const reject = useCallback((err: unknown) => {
    const code = apiCode(err);
    if (code === 'INVALID_CONTENT') return; // keep typing; markers show what is missing, preview is blocked
    setScenes(fromProject(serverRef.current));
    if (code === 'DURATION_BUDGET') setLimitHit('Video length reached. Shorten another scene to add more narration.');
    else toast.error(apiMessage(err, 'Could not save your changes'));
  }, []);
  const { schedule, flush, saving, dirty } = useAutosave(save, reject);
  useEffect(
    () => () => {
      flush();
    },
    [flush]
  );

  const apply = (next: LocalScene[]) => {
    const nextTotal = next.reduce((s, x) => s + itemSeconds(x.narration, x.actualSeconds), 0);
    if (!isEditAllowed(total, nextTotal, budget)) {
      setLimitHit('Video length reached. Shorten another scene to add more narration.');
      return;
    }
    setLimitHit(null);
    setScenes(next);
    schedule(next);
  };
  const move = (from: number, to: number) => {
    if (to < 0 || to >= scenes.length) return;
    const next = [...scenes];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    apply(next);
  };
  const onDragEnd = (r: DropResult) => {
    if (!r.destination || r.destination.index === r.source.index) return;
    move(r.source.index, r.destination.index);
  };
  const addScene = () => {
    const id = `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    apply([
      ...scenes,
      {
        id,
        type: 'statement',
        content: SCENE_TEMPLATES.statement.empty(),
        narration: '',
        reasoning: null,
        actualSeconds: null
      }
    ]);
  };
  const changeType = async (sceneId: string, type: SceneType) => {
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene || scene.type === type) return;
    const known = serverRef.current.scenes.some((s) => s.id === sceneId);
    try {
      setBusy(sceneId);
      await flush();
      if (known) {
        const res: any = await API.changeExplainerSceneType({ projectId: project.id, sceneId, type });
        serverRef.current = res.data;
        onProject(res.data as VideoProject);
        setScenes(fromProject(res.data));
      } else {
        apply(
          scenes.map((s) =>
            s.id === sceneId
              ? { ...s, type, content: { ...SCENE_TEMPLATES[type].empty(), title: s.content.title ?? '' } }
              : s
          )
        );
      }
    } catch (err) {
      toast.error(apiMessage(err, 'The template could not be changed. Try again.'));
    } finally {
      setBusy(null);
    }
  };
  const improve = async (instruction: string | null) => {
    if (!improveId) return;
    try {
      setBusy(improveId);
      await flush();
      const res: any = await API.improveExplainerScene({ projectId: project.id, sceneId: improveId, instruction });
      serverRef.current = res.data;
      onProject(res.data as VideoProject);
      setScenes(fromProject(res.data));
      setImproveId(null);
    } catch (err) {
      const code = apiCode(err);
      toast.error(
        code === 'DURATION_BUDGET'
          ? 'The improved scene would exceed the video length. Shorten another scene first.'
          : apiMessage(err, 'Elliott could not improve this scene. Try again.')
      );
    } finally {
      setBusy(null);
    }
  };
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const regenerate = async () => {
    setConfirmRegenerate(false);
    try {
      setBusy('regenerate');
      const res: any = await API.draftExplainerStoryboard({ projectId: project.id });
      serverRef.current = res.data;
      onProject(res.data as VideoProject);
      setScenes(fromProject(res.data));
    } catch (err) {
      toast.error(apiMessage(err, 'Elliott could not regenerate the storyboard. Try again.'));
    } finally {
      setBusy(null);
    }
  };
  const preview = async () => {
    if (incomplete) {
      toast.error('Some scenes are incomplete. Fill the highlighted fields first.');
      return;
    }
    await flush();
    onPreview();
  };

  const scenesLabel = `${scenes.length} scene${scenes.length === 1 ? '' : 's'}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }} data-testid="storyboard-editor">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: vb.font, fontSize: 18, fontWeight: 800, color: vb.text }}>
              Storyboard · {lessonName}
            </Typography>
            <Box
              sx={{
                fontFamily: vb.font,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.1em',
                px: 0.7,
                py: 0.25,
                borderRadius: '4px',
                background: vb.border,
                color: vb.textSecondary
              }}
            >
              EXPLAINER
            </Box>
            {!editing && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.4,
                  fontFamily: vb.font,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  color: vb.textTertiary
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 12 }} /> LOCKED
              </Box>
            )}
          </Box>
          <Typography sx={{ fontFamily: vb.font, fontSize: 12.5, color: vb.textTertiary }}>
            {scenesLabel} + standard opening & closing · Elliott narration
          </Typography>
        </Box>
        <Button
          onClick={() => setConfirmRegenerate(true)}
          disabled={busy !== null}
          sx={pillButtonSx()}
          data-testid="storyboard-regenerate"
        >
          {busy === 'regenerate' ? (
            'Regenerating...'
          ) : (
            <>
              Regenerate{' '}
              <Box component="span" sx={{ color: vb.textTertiary, ml: 0.5 }}>
                · free
              </Box>
            </>
          )}
        </Button>
        <Button onClick={() => setEditing((e) => !e)} sx={pillButtonSx()} data-testid="storyboard-toggle-edit">
          {editing ? 'Done Editing' : 'Edit Storyboard'}
        </Button>
      </Box>

      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, overflowY: 'auto', pr: 0.5, flex: 1, minHeight: 0 }}
      >
        <Box sx={lockedCardSx} data-testid="scene-opening">
          <OpeningPreview kit={kit} moduleName={moduleName} lessonName={lessonName} width={120} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockOutlinedIcon sx={{ fontSize: 14, color: vb.textTertiary }} />
              <Typography sx={{ fontFamily: vb.font, fontSize: 13, fontWeight: 800, color: vb.text }}>
                Standard opening · 3s
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary, mt: 0.4 }}>
              {moduleName} · {lessonName} over your brand kit, with the animated Dosen mark. Also used as the video
              thumbnail. Same on every video.
            </Typography>
          </Box>
        </Box>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="storyboard" isDropDisabled={!editing}>
            {(drop) => (
              <Box
                ref={drop.innerRef}
                {...drop.droppableProps}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}
              >
                {scenes.map((s, i) => (
                  <Draggable key={s.id} draggableId={s.id} index={i} isDragDisabled={!editing}>
                    {(drag, snapshot) => (
                      <Box
                        ref={drag.innerRef}
                        {...drag.draggableProps}
                        data-testid={`scene-${i}`}
                        sx={{
                          borderRadius: '12px',
                          border: `1px solid ${snapshot.isDragging ? vb.brand : vb.border}`,
                          background: vb.page,
                          p: 1.5,
                          display: 'flex',
                          gap: 1.5
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                          <Box
                            {...drag.dragHandleProps}
                            aria-label={`Drag scene ${i + 1}`}
                            sx={{
                              color: editing ? vb.textTertiary : 'transparent',
                              cursor: editing ? 'grab' : 'default',
                              display: 'flex'
                            }}
                          >
                            <DragIndicatorIcon sx={{ fontSize: 18 }} />
                          </Box>
                          {editing && (
                            <>
                              <IconButton
                                size="small"
                                aria-label={`Move scene ${i + 1} up`}
                                disabled={i === 0}
                                onClick={() => move(i, i - 1)}
                                sx={{ color: vb.textTertiary, p: 0.25 }}
                              >
                                <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label={`Move scene ${i + 1} down`}
                                disabled={i === scenes.length - 1}
                                onClick={() => move(i, i + 1)}
                                sx={{ color: vb.textTertiary, p: 0.25 }}
                              >
                                <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </>
                          )}
                        </Box>
                        <ScenePreview type={s.type} content={s.content} kit={kit} width={120} />
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                              sx={{
                                fontFamily: vb.font,
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: '0.1em',
                                color: vb.brand
                              }}
                            >
                              SCENE {i + 1}
                            </Typography>
                            {editing ? (
                              <Select
                                size="small"
                                value={s.type}
                                onChange={(e) => {
                                  changeType(s.id, e.target.value as SceneType);
                                }}
                                disabled={busy !== null}
                                inputProps={{
                                  'aria-label': `Scene ${i + 1} template`,
                                  'data-testid': `scene-${i}-type`
                                }}
                                sx={{
                                  color: vb.text,
                                  fontFamily: vb.font,
                                  fontSize: 12.5,
                                  background: vb.surface,
                                  '& .MuiOutlinedInput-notchedOutline': { borderColor: vb.border },
                                  '& .MuiSvgIcon-root': { color: vb.textTertiary },
                                  minWidth: 160
                                }}
                                MenuProps={{
                                  PaperProps: {
                                    sx: { background: vb.surface, color: vb.text, border: `1px solid ${vb.border}` }
                                  }
                                }}
                              >
                                {SCENE_TYPES.map((t) => (
                                  <MenuItem key={t} value={t} sx={{ fontFamily: vb.font, fontSize: 13 }}>
                                    {SCENE_TEMPLATES[t].label}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Box
                                sx={{
                                  fontFamily: vb.font,
                                  fontSize: 10,
                                  fontWeight: 800,
                                  letterSpacing: '0.08em',
                                  px: 0.7,
                                  py: 0.25,
                                  borderRadius: '4px',
                                  background: vb.border,
                                  color: vb.textSecondary
                                }}
                              >
                                {SCENE_TEMPLATES[s.type].label.toUpperCase()}
                              </Box>
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Typography
                              sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary }}
                              data-testid={`scene-${i}-seconds`}
                            >
                              {formatApprox(itemSeconds(s.narration, s.actualSeconds))}
                            </Typography>
                            {problems[i].length > 0 && (
                              <Tooltip title={problems[i].join(' · ')}>
                                <Box
                                  sx={{
                                    fontFamily: vb.font,
                                    fontSize: 10,
                                    fontWeight: 800,
                                    px: 0.6,
                                    py: 0.2,
                                    borderRadius: '4px',
                                    background: 'rgba(255,107,107,0.15)',
                                    color: vb.danger,
                                    cursor: 'default'
                                  }}
                                  data-testid={`scene-${i}-incomplete`}
                                >
                                  INCOMPLETE
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                          {editing ? (
                            <>
                              <SceneFields
                                type={s.type}
                                content={s.content}
                                index={i}
                                onChange={(content) =>
                                  apply(scenes.map((x) => (x.id === s.id ? { ...x, content } : x)))
                                }
                              />
                              <Box>
                                <Typography sx={labelSx}>Narration</Typography>
                                <TextField
                                  multiline
                                  minRows={2}
                                  fullWidth
                                  value={s.narration}
                                  onChange={(e) =>
                                    apply(
                                      scenes.map((x) =>
                                        x.id === s.id ? { ...x, narration: e.target.value, actualSeconds: null } : x
                                      )
                                    )
                                  }
                                  inputProps={{
                                    'aria-label': `Scene ${i + 1} narration`,
                                    'data-testid': `scene-${i}-narration`
                                  }}
                                  sx={fieldSx}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {s.reasoning && (
                                  <Typography
                                    sx={{ fontFamily: vb.font, fontSize: 11.5, color: vb.textTertiary, flex: 1 }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{ fontWeight: 800, letterSpacing: '0.08em', color: vb.textTertiary }}
                                    >
                                      ELLIOTT&apos;S REASONING{' '}
                                    </Box>
                                    {s.reasoning}
                                  </Typography>
                                )}
                                <Box sx={{ flex: 1 }} />
                                <Button
                                  size="small"
                                  onClick={() => setImproveId(s.id)}
                                  disabled={busy !== null}
                                  sx={{ ...pillButtonSx(), py: 0.5, fontSize: 12 }}
                                  data-testid={`scene-${i}-improve`}
                                >
                                  Improve with Elliott
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => apply(scenes.filter((x) => x.id !== s.id))}
                                  disabled={busy !== null || scenes.length <= 1}
                                  sx={{ ...pillButtonSx(), py: 0.5, fontSize: 12, color: vb.textSecondary }}
                                  data-testid={`scene-${i}-delete`}
                                >
                                  Delete
                                </Button>
                              </Box>
                            </>
                          ) : (
                            <>
                              <Typography
                                sx={{ fontFamily: vb.font, fontSize: 14, fontWeight: 700, color: vb.text }}
                                noWrap
                              >
                                {sceneSummaryLine(s.type, s.content)}
                              </Typography>
                              <Typography sx={{ fontFamily: vb.font, fontSize: 12.5, color: vb.textSecondary }}>
                                <Box
                                  component="span"
                                  sx={{
                                    fontWeight: 800,
                                    letterSpacing: '0.08em',
                                    color: vb.textTertiary,
                                    fontSize: 10.5
                                  }}
                                >
                                  NARRATION{' '}
                                </Box>
                                {s.narration}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {drop.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>

        {editing && (
          <Box
            component="button"
            type="button"
            onClick={addScene}
            data-testid="scene-add"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              background: 'transparent',
              border: `1px dashed ${vb.borderStrong}`,
              color: vb.textSecondary,
              borderRadius: '12px',
              px: 1.5,
              py: 1.25,
              fontFamily: vb.font,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Add Scene
          </Box>
        )}

        <Box sx={lockedCardSx} data-testid="scene-closing">
          <ClosingPreview kit={kit} width={120} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockOutlinedIcon sx={{ fontSize: 14, color: vb.textTertiary }} />
              <Typography sx={{ fontFamily: vb.font, fontSize: 13, fontWeight: 800, color: vb.text }}>
                Standard closing · 2s
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary, mt: 0.4 }}>
              Animated Dosen mark with a soft fade-out. Same on every video.
            </Typography>
          </Box>
        </Box>
      </Box>

      {limitHit && (
        <Typography
          role="alert"
          sx={{ fontFamily: vb.font, fontSize: 12.5, color: vb.danger }}
          data-testid="limit-hint"
        >
          {limitHit}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          pt: 1.5,
          borderTop: `1px solid ${vb.border}`
        }}
      >
        <RuntimeMeter
          label="Total runtime"
          narrationSeconds={total}
          lockedSeconds={EXPLAINER_LOCKED_SECONDS}
          targetSeconds={project.targetDurationSeconds}
          budgetSeconds={budget}
        />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary }}>
          {saving ? 'Saving...' : dirty ? 'Unsaved' : 'Nothing is saved until you save the video'}
        </Typography>
        <Button onClick={onBackToGuidance} sx={pillButtonSx()}>
          Guidance
        </Button>
        <Button
          data-testid="preview-video"
          onClick={preview}
          disabled={busy !== null || incomplete || total > budget + 0.05}
          sx={pillButtonSx(true)}
        >
          Preview Video
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmRegenerate}
        title="Regenerate the whole storyboard?"
        message="Your edits will be replaced by a fresh storyboard. This is free."
        confirmLabel="Regenerate"
        onConfirm={regenerate}
        onClose={() => setConfirmRegenerate(false)}
      />
      <ImproveDialog
        open={improveId !== null}
        title="Improve this scene"
        busy={busy === improveId && improveId !== null}
        onClose={() => setImproveId(null)}
        onSubmit={improve}
      />
    </Box>
  );
};

export default StoryboardEditor;
